import React from 'react';
import { useController } from 'react-hook-form';
import styled from 'styled-components';
import { OperandType } from '../../../../../utils/apollo/graphqlSchema';
import { IconName, SvgIcon } from '../../../../Icon';
import { colors } from '../../../../../config/styles';
import KubitTooltip from '../../../../KubitTooltip';
import { IPredicateFormFieldPath } from '../../../types';
import useFilterWidgetFormContext from '../../../utils/useFilterWidgetFormContext';
import { getPredicateFormFieldPath } from '../../../utils/formPath';
import { theme } from '../../../../../styles/theme';

interface IProps {
  formPath: IPredicateFormFieldPath;
}

const operandToggleConfig: Record<
  OperandType,
  { icon: IconName; hint: string; hoverColor: string }
> = {
  [OperandType.Field]: {
    icon: 'operand-field',
    hint: 'Change to value',
    hoverColor: colors.purple,
  },
  [OperandType.Value]: {
    icon: 'operand-value',
    hint: 'Change to field',
    hoverColor: theme.colors.mainBlue,
  },
};

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const OperandTypeIcon = styled(SvgIcon)<{ $hoverColor: string }>`
  &:hover {
    fill: ${({ $hoverColor }) => $hoverColor};
  }
`;

const OperandTypeToggle = ({ formPath }: IProps) => {
  const fieldPath = getPredicateFormFieldPath(formPath);

  const { control, setValue, resetField } = useFilterWidgetFormContext();

  const { field } = useController({
    name: `${fieldPath}.operand.type`,
    control,
  });

  const handleClick = () => {
    const newValue =
      field.value === OperandType.Value ? OperandType.Field : OperandType.Value;

    field.onChange(newValue);

    setValue(`${fieldPath}.relOp`, null);
    resetField(`${fieldPath}.matchCase`, { defaultValue: true });

    if (newValue === OperandType.Field) {
      setValue(`${fieldPath}.operand`, {
        type: newValue,
        field: null,
        fromField: null,
        toField: null,
      });
    } else {
      setValue(`${fieldPath}.operand`, {
        type: newValue,
        operands: [],
        fromOperand: null,
        toOperand: null,
      });
    }
  };

  const { icon, hint, hoverColor } = operandToggleConfig[field.value];

  return (
    <KubitTooltip noOpenDelay={true} content={hint} placement="bottom-start">
      <Container>
        <OperandTypeIcon
          name={icon}
          width={16}
          height={16}
          onClick={handleClick}
          isHoverable
          $hoverColor={hoverColor}
        />
      </Container>
    </KubitTooltip>
  );
};

export default OperandTypeToggle;
